/**
 * Utilitarios compartilhados pelas tres telas do painel do influencer.
 *
 * Existe para tirar daqui a duplicacao que havia entre Dashboard, MyCampaigns e MyWallet:
 * cada uma tinha a sua copia de `money()` e `formatDate()`, e so uma das tres sabia
 * desembrulhar o erro da API.
 */
import i18n from '@/i18n/i18n'

/** Chaves do seletor de periodo do cabecalho. */
export const PERIODS = ['current_month', 'last_7', 'last_30', 'all']

export const DEFAULT_PERIOD = 'current_month'

/**
 * Datas da API vem em dois formatos: 'YYYY-MM-DD' (datas puras, como `available_at`) e
 * ISO com hora. `new Date('2026-08-12')` e interpretado como meia-noite UTC, que em BRT
 * cai no dia 11 — por isso a data pura e montada campo a campo, no fuso local.
 */
export function parseApiDate(value) {
  if (!value) return null
  if (value instanceof Date) return value

  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value))
  if (dateOnly) {
    return new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
  }

  const parsed = new Date(value)

  return Number.isNaN(parsed.getTime()) ? null : parsed
}

/** 'YYYY-MM-DD' no fuso local — e o formato que os filtros da API esperam. */
export function toApiDate(date) {
  const pad = n => String(n).padStart(2, '0')

  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
}

/**
 * Intervalo de datas de uma chave de periodo.
 *
 * `all` devolve `null`: a ausencia dos filtros e o que sinaliza "desde o inicio" para o
 * backend, e evita mandar uma data de inicio arbitraria.
 */
export function rangeFor(period) {
  const today = new Date()

  if (period === 'all') return null

  if (period === 'current_month') {
    return {
      start_date: toApiDate(new Date(today.getFullYear(), today.getMonth(), 1)),
      end_date: toApiDate(today),
    }
  }

  const days = period === 'last_7' ? 7 : 30
  const start = new Date(today)
  // -1 porque o intervalo inclui hoje: "ultimos 7 dias" sao hoje e os 6 anteriores.
  start.setDate(start.getDate() - (days - 1))

  return { start_date: toApiDate(start), end_date: toApiDate(today) }
}

/** Parametros de query no formato que o CrudTraitApi do backend coleta. */
export function periodParams(period) {
  const range = rangeFor(period)
  if (!range) return {}

  return {
    'custom_filters[start_date]': range.start_date,
    'custom_filters[end_date]': range.end_date,
  }
}

/** Rotulo do periodo para o titulo "Desempenho em agosto". */
export function periodLabel(period) {
  if (period === 'current_month') {
    return new Date().toLocaleDateString('pt-BR', { month: 'long' })
  }

  return i18n.t(`influencer_panel.period_${period}`).toLowerCase()
}

/** Chip de dia usado nas liberacoes e nos saques: { day: '12', month: 'AGO' }. */
export function dayChip(value) {
  const date = parseApiDate(value)
  if (!date) return { day: '--', month: '' }

  return {
    day: String(date.getDate()).padStart(2, '0'),
    month: date.toLocaleDateString('pt-BR', { month: 'short' }).replace('.', '').toUpperCase(),
  }
}

export function shortDate(value) {
  const date = parseApiDate(value)

  return date ? date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }) : ''
}

export function fullDate(value) {
  const date = parseApiDate(value)

  return date ? date.toLocaleDateString('pt-BR') : ''
}

/** Dias inteiros de hoje ate a data. Negativo quando ja passou. */
export function daysUntil(value) {
  const date = parseApiDate(value)
  if (!date) return null

  const startOfToday = new Date()
  startOfToday.setHours(0, 0, 0, 0)
  const target = new Date(date)
  target.setHours(0, 0, 0, 0)

  return Math.round((target - startOfToday) / 86400000)
}

/** Variacao percentual entre dois periodos. `null` quando nao ha base de comparacao. */
export function deltaPercent(current, previous) {
  const prev = Number(previous)
  if (!Number.isFinite(prev) || prev === 0) return null

  return ((Number(current) - prev) / Math.abs(prev)) * 100
}

/**
 * Mensagem de erro da API.
 *
 * `NotificationException` responde `{"messages":"[\"...\"]"}` — o `messages` e uma string
 * com JSON dentro, dai o duplo parse. Estava so no Dashboard; as outras duas telas
 * engoliam o erro com um catch vazio e mostravam a tela em branco.
 */
export function extractMessage(error, fallbackKey = 'influencer_panel.load_error') {
  const raw = error && error.data

  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw)
      if (parsed && parsed.messages) {
        try {
          const messages = JSON.parse(parsed.messages)
          if (Array.isArray(messages)) return messages.join(' ')
        } catch (e) { /* messages nao era JSON: usa a string crua abaixo */ }

        return parsed.messages
      }
    } catch (e) {
      return raw
    }
  }

  return i18n.t(fallbackKey)
}
